// When the app is offline, or a search request fails with a NETWORK error,
// card search, the deck editor's add autocomplete and the collection add
// autocomplete answer from the local card data instead, labelled "Offline
// results" (W5 step 5).
//
// Only where offline search exists (the installed PWA and the Android app,
// with the card data installed: client.offlineSearchAvailable). Everywhere
// else, and for every non-network failure (an HTTP error, a caller's abort),
// the online call's own result or error stands exactly as before.
import { ABORT_ERROR, AUTOCOMPLETE_ANSWER_KEY, NETWORK_MODE_ALWAYS } from './offlineCards.codes'
import { offlineBackend, offlineSearchAvailable } from './client'

const NETWORK_ERROR_NAMES = new Set(['TypeError', 'NetworkError', 'TimeoutError'])

/** A failure where no answer came back (fetch rejected), never an HTTP error or an abort. */
export function isNetworkFailure(err: unknown, signal?: AbortSignal | null): boolean {
  if (signal?.aborted) return false
  const name = (err as { name?: unknown } | null)?.name
  if (name === ABORT_ERROR) return false
  return typeof name === 'string' && NETWORK_ERROR_NAMES.has(name)
}

export function browserOffline(): boolean {
  return typeof navigator !== 'undefined' && navigator.onLine === false
}

/**
 * The online answer, or the offline one when offline search is available
 * and the device is offline (no request is sent) or the request failed with
 * a network error.
 */
export async function withOfflineFallback<T>(
  online: () => Promise<T>,
  offline: () => Promise<T>,
  signal?: AbortSignal | null,
): Promise<T> {
  if (offlineSearchAvailable() && browserOffline()) return offline()
  try {
    return await online()
  } catch (err) {
    if (offlineSearchAvailable() && isNetworkFailure(err, signal)) return offline()
    throw err
  }
}

/** An autocomplete answer and whether it came from the local card data. */
export interface AutocompleteAnswer {
  names: string[]
  offline: boolean
}

/** GET /cards/autocomplete with the offline fallback. */
export function autocompleteWithFallback(q: string, online: (q: string) => Promise<string[]>): Promise<AutocompleteAnswer> {
  return withOfflineFallback<AutocompleteAnswer>(
    async () => ({ names: await online(q), offline: false }),
    async () => ({ names: await offlineBackend().autocomplete(q), offline: true }),
  )
}

/**
 * The autocomplete query options with the fallback. Its own cache key: the
 * cached value is {names, offline}, not the string[] that other
 * ['autocomplete', q] readers (CoverPicker) keep.
 */
export function autocompleteQuery(query: string, fetchNames: (q: string) => Promise<string[]>) {
  return {
    queryKey: [AUTOCOMPLETE_ANSWER_KEY, query] as const,
    queryFn: (): Promise<AutocompleteAnswer> => autocompleteWithFallback(query, fetchNames),
    // React Query's default networkMode 'online' PAUSES a query while the
    // browser is offline, so the fallback above would never run. Only where
    // offline search can answer (installed app, card data installed) does
    // the query run regardless; everywhere else the default stands.
    ...(offlineSearchAvailable() ? { networkMode: NETWORK_MODE_ALWAYS } : {}),
  }
}

// Arrays an offline fallback produced, for callers whose state keeps the
// array itself (useInstantSearch): marks without changing the item shape.
const offlineArrays = new WeakSet<object>()

export function markOffline<T extends object>(arr: T): T {
  offlineArrays.add(arr)
  return arr
}

export function isOfflineResult(arr: object | null | undefined): boolean {
  return !!arr && offlineArrays.has(arr)
}
